import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useUserPreferences } from '~/redux/api/rolodex/useUserPreferences';

import { useSidebarNavPreference } from './SidebarNavPreferenceContext';

const MOBILE_BREAKPOINT = 768;

/** Keep in sync with `.sidebar-aside` in AppSidebarNav.css. */
export const SIDEBAR_NAV_WIDTH = 240;
/** Keep in sync with `.sidebar-aside--collapsed` in AppSidebarNav.css. */
export const SIDEBAR_NAV_COLLAPSED_WIDTH = 72;

interface SidebarNavContextValue {
  isCollapsed: boolean;
  isMobile: boolean;
  toggleCollapsed: () => void;
  isSidebarLayoutActive: boolean;
  width: number;
}

const SidebarNavContext = createContext<SidebarNavContextValue>({
  isCollapsed: false,
  isMobile: false,
  toggleCollapsed: () => {},
  isSidebarLayoutActive: false,
  width: 0,
});

export const SidebarNavProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { sidebarNavCollapsed, setSidebarNavCollapsed } = useUserPreferences();
  // Mobile forces the drawer shut without touching the persisted preference,
  // so reopening it there doesn't overwrite the desktop choice.
  const [isMobileCollapsed, setIsMobileCollapsed] = useState(false);

  // Initialise synchronously so the first render already knows the viewport,
  // preventing a flash of the desktop sidebar on narrow screens.
  const [isMobile, setIsMobile] = useState<boolean>(
    () => window.innerWidth < MOBILE_BREAKPOINT,
  );

  useEffect(() => {
    let prevMobile = window.innerWidth < MOBILE_BREAKPOINT;
    let debounceTimeout: ReturnType<typeof setTimeout> | undefined;

    // Debounced because dragging a window edge (or scrubbing a devtools
    // viewport slider) through the breakpoint fires dozens of resize events a
    // second. Reacting to each one would re-trigger the mobile/desktop layout
    // change every frame instead of once the size settles.
    const applyResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      if (mobile === prevMobile) return;
      prevMobile = mobile;
      setIsMobile(mobile);
      // Entering mobile: hide the sidebar so content fills the screen.
      if (mobile) setIsMobileCollapsed(true);
    };

    const handleResize = () => {
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(applyResize, 120);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(debounceTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const toggleCollapsed = useCallback(() => {
    if (isMobile) {
      setIsMobileCollapsed((prev) => !prev);
      return;
    }
    setSidebarNavCollapsed((prev) => !prev);
  }, [isMobile, setSidebarNavCollapsed]);

  const isSidebarNavAvailable = useFlag(FlagType.SidebarNav);
  const { isOptedIn } = useSidebarNavPreference();
  const isSidebarLayoutActive = isSidebarNavAvailable && isOptedIn;
  const isCollapsed = isMobile ? isMobileCollapsed : sidebarNavCollapsed;
  let width = 0;
  if (isSidebarLayoutActive && !isMobile) {
    width = isCollapsed ? SIDEBAR_NAV_COLLAPSED_WIDTH : SIDEBAR_NAV_WIDTH;
  }

  const value = useMemo(
    () => ({
      isCollapsed,
      isMobile,
      toggleCollapsed,
      isSidebarLayoutActive,
      width,
    }),
    [isCollapsed, isMobile, toggleCollapsed, isSidebarLayoutActive, width],
  );

  return (
    <SidebarNavContext.Provider value={value}>
      {children}
    </SidebarNavContext.Provider>
  );
};

export const useSidebarNav = () => useContext(SidebarNavContext);
